import * as React from "react"
import { Check, ChevronDown } from "lucide-react"
import { cn } from "@/lib/utils"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"

interface MultiSelectDropdownProps {
  options: string[]
  value: string[]
  onChange: (next: string[]) => void
  placeholder?: string
  disabled?: boolean
  className?: string
  allLabel?: string
  emptyContent?: React.ReactNode
}

export function MultiSelectDropdown({
  options,
  value,
  onChange,
  placeholder = "Select options",
  disabled = false,
  className,
  allLabel = "All schemas",
  emptyContent,
}: MultiSelectDropdownProps) {
  const selected = Array.isArray(value) ? value : []
  const allSelected = options.length > 0 && selected.length === options.length

  const toggleOne = (option: string) => {
    if (selected.includes(option)) {
      onChange(selected.filter((item) => item !== option))
      return
    }
    onChange([...new Set([...selected, option])])
  }

  const toggleAll = () => {
    onChange(allSelected ? [] : options)
  }

  const triggerLabel =
    selected.length === 0
      ? placeholder
      : allSelected
        ? `${allLabel} (${selected.length})`
        : `${selected.length} selected`

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          className={cn(
            "flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-start text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
            className
          )}
        >
          <span className="truncate">{triggerLabel}</span>
          <ChevronDown className="size-4 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-1">
        <div className="max-h-72 overflow-y-auto">
          {options.length > 0 ? (
            <>
              <button
                type="button"
                onClick={toggleAll}
                className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm hover:bg-accent hover:text-accent-foreground"
              >
                <span className="flex size-4 items-center justify-center rounded-sm border">
                  {allSelected ? <Check className="size-3" /> : null}
                </span>
                <span>{allLabel}</span>
              </button>
              <div className="my-1 h-px bg-muted" />
              {options.map((option) => {
                const checked = selected.includes(option)
                return (
                  <button
                    key={option}
                    type="button"
                    onClick={() => toggleOne(option)}
                    className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm hover:bg-accent hover:text-accent-foreground"
                  >
                    <span className="flex size-4 items-center justify-center rounded-sm border">
                      {checked ? <Check className="size-3" /> : null}
                    </span>
                    <span className="truncate">{option}</span>
                  </button>
                )
              })}
            </>
          ) : (
            <div className="px-2 py-2 text-sm text-muted-foreground">{emptyContent || "No options available"}</div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}
